import {Field} from 'formik';
import {useEffect, useState, useCallback, useRef} from 'react';

import {
    Alert,
    Button,
    Checkbox,
    Container,
    Fieldset,
    Group,
    Text,
    Title,
    ActionIcon,
    Tooltip
} from '@mantine/core';
import { AiOutlinePlus } from 'react-icons/ai';
import {FileUploadInput} from "../inputs/FileUploadInput.tsx";
import {useRegistrars} from "../../registrars";
import {FormikSelect} from '@flexinet/ui-components';
import {Registrar} from "../../registrars/types";
import fileProcessingService from "../services/FileProcessingService";
import { AddRegistrarModal } from '../../domain-names/components/AddRegistrarModal';

// Simple FormikCheckbox wrapper component
interface FormikCheckboxProps {
    field: {
        name: string;
        value: boolean;
    };
    form: {
        setFieldValue: (field: string, value: boolean) => void;
    };
    label?: string;
    description?: string;
}

const FormikCheckbox = ({field, form, ...props}: FormikCheckboxProps) => {
    return (
        <Checkbox
            name={field.name}
            checked={field.value}
            onChange={(event) => form.setFieldValue(field.name, event.currentTarget.checked)}
            {...props}
        />
    );
};

export const UploadFormComponent = ({ onFileChange }: { onFileChange?: (file: File | null) => void }) => {
    const { items: registrars, fetchRecords } = useRegistrars();
    const [showAddRegistrarModal, setShowAddRegistrarModal] = useState(false);
    const hasFetchedRef = useRef(false);

    // Fetch registrars on component mount - only once
    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchRecords();
            hasFetchedRef.current = true;
        }
        // FNP-960: deliberately mount-only. The callback is recreated on
        // every render, so adding it to the deps would re-fire in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run once on mount

    const handleRegistrarCreated = useCallback((_newRegistrar: Registrar) => {
        setShowAddRegistrarModal(false);
        // Refresh the registrars list after creating a new one
        fetchRecords();
        // FNP-960: deliberately mount-only. The callback is recreated on
        // every render, so adding it to the deps would re-fire in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Remove fetchRecords from dependency to avoid circular calls

    const registrarOptions = (registrars || []).map((registrar: Registrar) => ({
        value: registrar.id,
        label: registrar.name || registrar.id,
    }));

    return (
        <Container>
            <Fieldset legend={<Title order={3}><Text tt={'uppercase'}>Upload Configuration</Text></Title>} px={'xl'} mb={'xl'}>
                
                <Alert variant="light" color="blue" title="File Format Requirements" mb="xl">
                    <Text size="sm">
                        Please upload a CSV or ODS file with the following columns:
                    </Text>
                    <Text size="sm" mt="xs">
                        • <strong>Domain Name</strong> (required) - Full domain name (e.g., example.com)
                    </Text>
                    <Text size="sm">
                        • <strong>Expiration Date</strong> (optional) - Format: YYYY-MM-DD
                    </Text>
                    <Text size="sm">
                        • <strong>Ownership Date</strong> (optional) - Format: YYYY-MM-DD
                    </Text>
                    <Text size="sm">
                        • <strong>Estimated Value</strong> (optional) - Will be ignored during import
                    </Text>
                    <Text size="sm" mt="xs" c="dimmed">
                        Maximum file size: 10MB
                    </Text>
                    <Group mt="md">
                        <Button 
                            variant="light" 
                            size="sm"
                            onClick={() => fileProcessingService.downloadTemplate()}
                        >
                            Download Template File
                        </Button>
                    </Group>
                </Alert>

                <Field
                    name='fileName'
                    label='File'
                    description='Select your CSV or ODS file containing domain names'
                    component={FileUploadInput}
                    onFileChange={onFileChange}
                    skipUpload={true}
                    withAsterisk
                    my={'xl'}
                />

                <Group align="end" gap="sm">
                    <div style={{ flex: 1 }}>
                        <Field
                            name='defaultRegistrarId'
                            label='Default Registrar'
                            description='All imported domain names will be assigned to this registrar'
                            component={FormikSelect}
                            data={registrarOptions}
                            placeholder="Select a registrar"
                            withAsterisk
                            my={'xl'}
                        />
                    </div>
                    <Tooltip label="Add new registrar">
                        <ActionIcon
                            variant="light"
                            color="blue"
                            size="lg"
                            onClick={() => setShowAddRegistrarModal(true)}
                            style={{ marginBottom: '1rem' }}
                        >
                            <AiOutlinePlus />
                        </ActionIcon>
                    </Tooltip>
                </Group>

            </Fieldset>

            <Fieldset legend={<Title order={3}><Text tt={'uppercase'}>Import Options</Text></Title>} px={'xl'} mb={'xl'}>
                
                <Group mt="md">
                    <Field
                        name='skipDuplicates'
                        component={FormikCheckbox}
                        label="Skip duplicate domain names"
                        description="Skip domains that already exist in your account"
                    />
                </Group>

                <Group mt="md">
                    <Field
                        name='updateExisting'
                        component={FormikCheckbox}
                        label="Update existing domain names"
                        description="Update existing domains with new information from the file"
                    />
                </Group>

            </Fieldset>

            {/* Add Registrar Modal */}
            <AddRegistrarModal
                isOpen={showAddRegistrarModal}
                onClose={() => setShowAddRegistrarModal(false)}
                onRegistrarCreated={handleRegistrarCreated}
            />

        </Container>
    );
};